import React from 'react'
import { Modal as BootstrapModal } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'

import { closeModal } from 'store/redux-slices/modal'
import Button from 'components/Common/Button'
import { BUTTON_VARIANT } from 'utils/constants'
import './styles/index.scss'
import { ALLOWED_BASE_MODAL_TYPES } from '../../constants/modals'

export const BaseModal = ({
  show,
  centered = true,
  backdrop,
  keyboard,
  size,
  className,
  scrollable,
  title,
  modalProps = {},
  graphic,
  buttons,
  body,
  handleClose = () => {},
  modalType
}) => {
  if (!ALLOWED_BASE_MODAL_TYPES.includes(modalType)) {
    console.error('Invalid modal type.')
    return null
  }

  return (
    <BootstrapModal
      show={show}
      onHide={handleClose}
      centered={centered}
      backdrop={backdrop}
      keyboard={keyboard}
      size={size}
      className={`common-modal ${className}`}
      scrollable={scrollable}
    >
      {title && (
        <BootstrapModal.Header closeButton={modalProps.showCloseButton !== false}>
          {graphic && (
            <div className='modal-graphic'>
              {graphic}
            </div>
          )}
          <BootstrapModal.Title>{title}</BootstrapModal.Title>
        </BootstrapModal.Header>
      )}

      <BootstrapModal.Body>
        {typeof body === 'function' ? body() : body}
      </BootstrapModal.Body>

      {buttons && buttons.length > 0 && (
        <BootstrapModal.Footer>
          <div className='modal-btns'>
            {buttons?.slice(0, 4)?.map((button, index) => {
              const buttonText = button.text
              return (
                <Button
                  key={index}
                  variant={button.variant || BUTTON_VARIANT.PRIMARY}
                  onClick={() => {
                    if (button.onClick) {
                      button.onClick()
                    }
                    if (button.closeOnClick !== false) {
                      handleClose()
                    }
                  }}
                  className={`modal-btn ${button.className || ''}`}
                  disabled={button.disabled || false}
                >
                  {buttonText}
                </Button>
              )
            })}
          </div>
        </BootstrapModal.Footer>
      )}
    </BootstrapModal>
  )
}

/**
 * Common Modal component that uses react-bootstrap for UI and redux for state management.
 * Supports multiple modals, title, body, and 0-4 buttons in the footer.
 *
 * The modal state is managed through Redux. To open a modal, dispatch the openModal action:
 *
 * dispatch(openModal({
 *   name: MODALS.FORFEIT, // Required: Must be one of the values from the MODALS constant
 *   title: 'Modal Title',
 *   body: <div>Modal Content</div>,
 *   // Optional: Add a React node to display an illustration/icon at the top
 *   graphic: <img src="/path/to/illustration.svg" alt="Info" />,
 *   buttons: [
 *     {
 *       text: 'Cancel',
 *       // IMPORTANT: Use BUTTON_VARIANT constants, not raw strings
 *       variant: BUTTON_VARIANT.SECONDARY,
 *       onClick: () => console.log('Cancel clicked')
 *     },
 *     {
 *       text: 'Confirm',
 *       variant: BUTTON_VARIANT.PRIMARY,
 *       onClick: () => console.log('Confirm clicked')
 *     }
 *   ]
 * }))
 *
 * To close a modal, dispatch the closeModal action with the modal name:
 * - Modal name is required: An error will be thrown if no name is provided
 */
const Modal = () => {
  const dispatch = useDispatch()
  const { modals } = useSelector((state) => state.modal)

  // If there are no modals, don't render anything
  if (!modals || modals.length === 0) {
    return null
  }

  return (
    <>
      {modals.map((modal, modalIndex) => {
        const { isOpen, name, title, body, buttons, graphic, modalProps = {}, onClose } = modal

        const handleClose = () => {
          if (typeof onClose === 'function') {
            onClose()
          }
          // Close the modal by its name
          dispatch(closeModal(name))
        }

        return (
          <BaseModal
            key={modalIndex}
            modalType='REDUX_MODAL'
            show={isOpen}
            handleClose={handleClose}
            centered
            backdrop={modalProps.backdrop !== undefined ? modalProps.backdrop : 'static'}
            keyboard={modalProps.keyboard !== undefined ? modalProps.keyboard : true}
            size={modalProps.size || 'md'}
            className={`${name} ${modalProps.className || ''}`}
            scrollable={modalProps.scrollable !== undefined ? modalProps.scrollable : true}
            title={title}
            modalProps={modalProps}
            graphic={graphic}
            buttons={buttons}
            body={body}
          />
        )
      })}
    </>
  )
}

export default Modal
